const YM_COUNTER_ID = Number(import.meta.env.VITE_YM_COUNTER_ID || 104839079);

const GOAL_FIRED_PREFIX = "ym_goal:";

type YmFunction = (counterId: number, action: string, ...args: unknown[]) => void;

/**
 * Достаёт ClientID Яндекс.Метрики. Счётчик грузится асинхронно и может быть
 * вырезан блокировщиком, поэтому ждём его не дольше таймаута и в любом случае
 * отдаём undefined, а не блокируем вызывающий код.
 */
export const getMetrikaClientId = (timeoutMs = 500): Promise<string | undefined> => {
  return new Promise(resolve => {
    const ym = (window as unknown as { ym?: YmFunction }).ym;

    if (typeof ym !== "function") {
      resolve(undefined);
      return;
    }

    let settled = false;
    const finish = (value?: string): void => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(value);
    };

    const timer = window.setTimeout(() => finish(undefined), timeoutMs);

    try {
      ym(YM_COUNTER_ID, "getClientID", (clientId: string) => {
        window.clearTimeout(timer);
        finish(clientId);
      });
    } catch {
      window.clearTimeout(timer);
      finish(undefined);
    }
  });
};

/**
 * Отправляет JS-цель в Яндекс.Метрику. Счётчик может быть не загружен или
 * вырезан блокировщиком — в этом случае событие молча отбрасывается.
 */
export const sendMetrikaGoal = (target: string, params?: Record<string, unknown>): void => {
  try {
    const ym = (window as unknown as { ym?: YmFunction }).ym;
    if (typeof ym !== "function") {
      return;
    }
    if (params) {
      ym(YM_COUNTER_ID, "reachGoal", target, params);
      return;
    }
    ym(YM_COUNTER_ID, "reachGoal", target);
  } catch {
    // Метрика не должна ронять бизнес-логику
  }
};

/**
 * Dedup событий по записи: поллинги и повторные рендеры могут дёргать
 * одно и то же событие несколько раз, а цель должна улететь один раз за сессию.
 */
export const hasGoalFired = (key: string): boolean => {
  try {
    return window.sessionStorage.getItem(GOAL_FIRED_PREFIX + key) === "1";
  } catch {
    return false;
  }
};

export const markGoalFired = (key: string): void => {
  try {
    window.sessionStorage.setItem(GOAL_FIRED_PREFIX + key, "1");
  } catch {
    // sessionStorage может быть недоступен (приватный режим) — не критично
  }
};

/**
 * Отправляет цель ровно один раз за сессию для указанного ключа.
 */
export const sendMetrikaGoalOnce = (key: string, target: string, params?: Record<string, unknown>): void => {
  if (hasGoalFired(key)) {
    return;
  }
  markGoalFired(key);
  sendMetrikaGoal(target, params);
};
